// ABOUTME: Search results display component for knowledge base content
// ABOUTME: Shows combined article and video results with filtering and navigation

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Chip,
  Tabs,
  Tab,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import { Article, VideoLibrary, CalendarToday, Visibility } from '@mui/icons-material';
import { KBSearchResultsDto } from '../../types/knowledgeBase';

interface SearchResultsProps {
  results: KBSearchResultsDto;
  query: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, query }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [contentType, setContentType] = React.useState<'all' | 'articles' | 'videos'>('all');

  const handleArticleClick = (articleId: number) => {
    navigate(`/knowledge-base/article/${articleId}`);
  };

  const handleVideoClick = (videoId: number) => {
    navigate(`/knowledge-base/video/${videoId}`);
  };

  const filteredArticles =
    contentType === 'all' || contentType === 'articles' ? results.articles : [];
  const filteredVideos =
    contentType === 'all' || contentType === 'videos' ? results.videos : [];

  const hasResults = results.totalResults > 0;

  return (
    <Box>
      {/* Results Header */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Search Results for &quot;{query}&quot;
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Found {results.totalResults} result{results.totalResults !== 1 ? 's' : ''} (
          {results.articles.length} article{results.articles.length !== 1 ? 's' : ''},{' '}
          {results.videos.length} video{results.videos.length !== 1 ? 's' : ''})
        </Typography>

        {/* Content Type Filter */}
        <Box mt={2}>
          <Tabs
            value={contentType}
            onChange={(_, value) => setContentType(value)}
            variant="fullWidth"
          >
            <Tab
              label={`All (${results.totalResults})`}
              value="all"
              icon={<Article />}
              iconPosition="start"
            />
            <Tab
              label={`Articles (${results.articles.length})`}
              value="articles"
              icon={<Article />}
              iconPosition="start"
            />
            <Tab
              label={`Videos (${results.videos.length})`}
              value="videos"
              icon={<VideoLibrary />}
              iconPosition="start"
            />
          </Tabs>
        </Box>
      </Paper>

      {/* No Results */}
      {!hasResults && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No results found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try different keywords or browse by category
          </Typography>
        </Box>
      )}

      {/* Article Results */}
      {filteredArticles.length > 0 && (
        <Box mb={4}>
          {contentType === 'all' && (
            <>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Article /> Articles
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {filteredArticles.map((article) => (
              <Box key={article.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' } }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    onClick={() => handleArticleClick(article.id)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    {article.thumbnailUrl && (
                      <CardMedia
                        component="img"
                        height="160"
                        image={article.thumbnailUrl}
                        alt={article.title}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Article fontSize="small" color="primary" />
                        <Typography variant="caption" color="primary">
                          Article
                        </Typography>
                      </Box>

                      <Typography variant="h6" component="div" gutterBottom>
                        {article.title}
                      </Typography>

                      {article.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {article.description}
                        </Typography>
                      )}

                      {/* Metadata */}
                      <Box display="flex" gap={2} mt={2} alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Visibility fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {article.viewCount}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(article.updatedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Category */}
                      {article.categoryName && (
                        <Box mt={1}>
                          <Chip label={article.categoryName} size="small" variant="outlined" />
                        </Box>
                      )}

                      {/* Tags */}
                      {article.tagNames.length > 0 && (
                        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                          {article.tagNames.slice(0, 3).map((tagName, idx) => (
                            <Chip key={idx} label={tagName} size="small" />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Video Results */}
      {filteredVideos.length > 0 && (
        <Box>
          {contentType === 'all' && (
            <>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoLibrary /> Videos
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {filteredVideos.map((video) => (
              <Box key={video.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' } }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    onClick={() => handleVideoClick(video.id)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    {video.thumbnailUrl && (
                      <Box position="relative">
                        <CardMedia
                          component="img"
                          height="160"
                          image={video.thumbnailUrl}
                          alt={video.title}
                        />
                        <Box
                          position="absolute"
                          bottom={8}
                          right={8}
                          bgcolor={alpha(theme.palette.common.black, 0.7)}
                          color="white"
                          px={1}
                          py={0.5}
                          borderRadius={1}
                        >
                          <Typography variant="caption">
                            {video.durationSeconds
                              ? `${Math.floor(video.durationSeconds / 60)}:${(
                                  video.durationSeconds % 60
                                )
                                  .toString()
                                  .padStart(2, '0')}`
                              : 'Video'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <VideoLibrary fontSize="small" color="secondary" />
                        <Typography variant="caption" color="secondary">
                          Video
                        </Typography>
                      </Box>

                      <Typography variant="h6" component="div" gutterBottom>
                        {video.title}
                      </Typography>

                      {video.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {video.description}
                        </Typography>
                      )}

                      {/* Metadata */}
                      <Box display="flex" gap={2} mt={2} alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Visibility fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {video.viewCount}
                          </Typography>
                        </Box>
                        <Chip label={video.audienceTag} size="small" color="primary" />
                      </Box>

                      {/* Tags */}
                      {video.tags.length > 0 && (
                        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                          {video.tags.slice(0, 3).map((tag) => (
                            <Chip key={tag.id} label={tag.name} size="small" />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SearchResults;
